import { useState, useEffect } from "react";
import { ROLES, normalizeRole, isStaffRole } from "../utils/roles";

/**
 * useAuth Hook
 * Centralizes access to localStorage("userLoggedIn")
 */
const useAuth = () => {
    const [user, setUser] = useState(null);

    // Helper to safely parse user data
    const getUserFromStorage = () => {
        try {
            const storedData = localStorage.getItem("userLoggedIn");
            return storedData ? JSON.parse(storedData) : null;
        } catch (error) {
            console.error("Error parsing user data from localStorage:", error);
            return null;
        }
    };

    // We use a simple read on every call to mimic existing behavior of components
    // reading directly from localStorage during render.
    // Using state/effect might cause flickering if we don't initialize synchronously,
    // but standard hooks usually initialize state.
    // HOWEVER, many existing components do `const user = JSON.parse(...)` at the top level.
    // If we change this to `useEffect`, we might break things that expect data immediately.
    // So we will initialize state directly from storage.

    const currentUser = getUserFromStorage();
    const roleString = currentUser?.role || null;
    const normalizedRole = normalizeRole(roleString);
    const isSupplier = normalizedRole === ROLES.SUPPLIER;

    return {
        user: currentUser,
        token: currentUser?.token,
        userId: currentUser?.user_id,
        roleId: currentUser?.role_id,
        organisationId: currentUser?.organisation_id,
        customerId: currentUser?.customer_id,
        // Only suppliers have a supplier id (TEP id, falling back to user id);
        // admins, managers and recruiters must not be scoped to a supplier.
        supplierId: isSupplier ? (currentUser?.supplierId || currentUser?.user_id) : null,
        role: roleString,
        // Role flags — use these instead of comparing role strings in components.
        isAdmin: normalizedRole === ROLES.ADMIN,
        isManager: normalizedRole === ROLES.MANAGER,
        isRecruiter: normalizedRole === ROLES.RECRUITER,
        isSupplier,
        isStaff: isStaffRole(normalizedRole),
        isAuthenticated: !!currentUser,
    };
};

export default useAuth;
